import { Hash, CheckSquare, HelpCircle, Terminal, Mic, Circle, Sparkles, Radio, Video, MessageSquare, Shield } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";

const ICONS = { Hash, CheckSquare, HelpCircle, Terminal, Mic };

export default function Sidebar({ channels, active, onSelect, users, onOpenVoice, onSelectUser, dms = [], onOpenDM, isAdmin = false, variant = "desktop" }) {
  const online = users.filter((u) => u.online);
  const sfx = variant === "mobile" ? "-mobile" : "";
  const navBtn = (isActive) =>
    `w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors group ${
      isActive ? "bg-red-950/50 text-white border border-red-900/40" : "text-gray-400 hover:bg-[#1E2029] hover:text-gray-200 border border-transparent"
    }`;

  return (
    <div className="h-full flex flex-col bg-[#121318] w-[260px] border-r border-[#2E3242]">
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-[#2E3242] shrink-0">
        <img src={LOGO_URL} alt="Recipe Remedy" className="w-9 h-9 object-contain" />
        <div className="min-w-0">
          <p className="font-display font-bold text-sm leading-tight truncate">Recipe Remedy</p>
          <p className="text-[10px] text-red-400 font-mono tracking-[0.2em]">DEVHUB</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3">
        <p className="px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Channels</p>
        <div className="space-y-0.5">
          {channels.map((c) => {
            const Icon = ICONS[c.icon] || Hash;
            const isActive = active === c.id;
            return (
              <button key={c.id} data-testid={`channel-item-${c.id}${sfx}`} onClick={() => onSelect(c.id)} className={navBtn(isActive)}>
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-red-400" : "text-gray-500 group-hover:text-gray-300"}`} />
                <span className="truncate font-medium">{c.name}</span>
              </button>
            );
          })}
        </div>

        <p className="mt-5 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Studio</p>
        <button data-testid={`nav-recordings${sfx}`} onClick={() => onSelect("recordings")} className={navBtn(active === "recordings")}>
          <Radio className={`w-4 h-4 shrink-0 ${active === "recordings" ? "text-red-400" : "text-gray-500 group-hover:text-gray-300"}`} />
          <span className="truncate font-medium">recording-studio</span>
        </button>
        <button data-testid={`nav-meetings${sfx}`} onClick={() => onSelect("meetings")} className={navBtn(active === "meetings")}>
          <Video className={`w-4 h-4 shrink-0 ${active === "meetings" ? "text-red-400" : "text-gray-500 group-hover:text-gray-300"}`} />
          <span className="truncate font-medium">meetings</span>
        </button>
        {isAdmin && (
          <button data-testid={`nav-admin${sfx}`} onClick={() => onSelect("admin")} className={navBtn(active === "admin")}>
            <Shield className={`w-4 h-4 shrink-0 ${active === "admin" ? "text-red-400" : "text-gray-500 group-hover:text-gray-300"}`} />
            <span className="truncate font-medium">admin dashboard</span>
          </button>
        )}

        <button data-testid={`sidebar-voice-button${sfx}`} onClick={onOpenVoice}
          className="mt-4 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg ruby-gradient text-white text-sm font-semibold border border-red-900/40 hover:opacity-90 transition-opacity">
          <Sparkles className="w-4 h-4" /> AI Assistant
        </button>

        {dms.length > 0 && (
          <>
            <p className="mt-6 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Direct Messages</p>
            <div className="space-y-0.5">
              {dms.map((d) => (
                <button key={d.dm_key} data-testid={`dm-item-${d.user.id}${sfx}`} onClick={() => onOpenDM?.(d.user.id)}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-[#1E2029] transition-colors">
                  <div className="relative shrink-0">
                    <img src={d.user.avatar} alt={d.user.name} className="w-7 h-7 rounded-full object-cover" />
                    <Circle className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${d.user.online ? "text-emerald-500 fill-emerald-500" : "text-gray-600 fill-gray-600"}`} />
                  </div>
                  <span className="text-sm truncate text-gray-300 flex-1 text-left">{d.user.name}</span>
                  {d.unread > 0 && (
                    <span className="min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{d.unread}</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        <p className="mt-6 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Members — {online.length} online</p>
        <div className="space-y-0.5">
          {users.map((u) => (
            <button key={u.id} data-testid={`member-${u.id}${sfx}`} onClick={() => onSelectUser?.(u)}
              className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-[#1E2029] transition-colors text-left">
              <div className="relative shrink-0">
                <img src={u.avatar} alt={u.name} className={`w-7 h-7 rounded-full object-cover ${u.online ? "" : "opacity-40 grayscale"}`} />
                <Circle className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${u.online ? "text-emerald-500 fill-emerald-500" : "text-gray-600 fill-gray-600"}`} />
              </div>
              <span className={`text-sm truncate ${u.online ? "text-gray-200" : "text-gray-500"}`}>{u.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
